import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/domains/organizations';
import { brandRepository } from '../repositories/brand.repository';
import { brandService } from '../services/brand.service';
import { supabaseBrandLogoStorage } from '../infrastructure/supabaseBrandLogoStorage';
import { contactsAdapter } from '../infrastructure/contactsAdapter';
import { brandKeys } from '../services/brand.keys';
import { toAddBrandErrorMessage } from '../validators/brand.validators';
import type { Brand, AddBrandData } from '../types/brand.types';

export type { AddBrandData };

export function useAddBrand() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useMutation({
    mutationFn: async (brandData: AddBrandData): Promise<Brand> => {
      if (!orgId) {
        throw new Error('No organization selected');
      }

      return brandService.handleAddBrandWithRollback(
        orgId,
        brandData,
        async (insertData) => brandRepository.createBrand(insertData),
        supabaseBrandLogoStorage,
        contactsAdapter
      );
    },
    onSuccess: (data: Brand) => {
      const createdBrand: Brand = {
        id: data.id,
        name: data.name,
        logo_url: data.logo_url ?? null,
      };

      // Optimistically update query data
      queryClient.setQueryData<Brand[]>(brandKeys.all(orgId), (currentBrands) => {
        const existingBrands = currentBrands ?? [];
        const withoutDuplicate = existingBrands.filter((b) => b.id !== createdBrand.id);
        return [...withoutDuplicate, createdBrand];
      });
      queryClient.setQueryData<Brand[]>(brandKeys.legacyBrands(), (currentBrands) => {
        const existingBrands = currentBrands ?? [];
        const withoutDuplicate = existingBrands.filter((b) => b.id !== createdBrand.id);
        return [...withoutDuplicate, createdBrand];
      });

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: brandKeys.all(orgId) });
      queryClient.invalidateQueries({ queryKey: brandKeys.legacyBrands() });
      queryClient.invalidateQueries({ queryKey: brandKeys.withActiveFilter(orgId) });
      queryClient.invalidateQueries({ queryKey: ['brands-with-active-filter'] });
      queryClient.invalidateQueries({ queryKey: brandKeys.allMetrics(orgId) });
      queryClient.invalidateQueries({ queryKey: ['all-brand-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });

      toast({
        title: 'Brand added successfully',
        description: `${data.name} has been added to your brand portfolio.`,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to add brand',
        description: toAddBrandErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
}
